import {makeAutoObservable, observable} from 'mobx';

import {UserSharedDTO} from '@/domain';
import {isArraySimpleEqual} from '@/domain/models/Model';
import {SharedProfileError} from '@/entities/user/model/errors';
import {ONE_SECOND_MS} from '@/shared/consts/time';

import {autoupdateCurrentTime} from './autoupdateCurrentTime';
import {
  AVAILABLE_AB_TESTS,
  FiltersModalView,
  ProfileModal,
  UserModal,
} from './types';

class AppStore {
  sharedProfile: UserSharedDTO | SharedProfileError | null = null;
  activeFilterModalView: FiltersModalView | undefined;
  private _currentTime = Date.now();
  private activeProfileModals = new Set<ProfileModal>();
  private enabledSignUpTests = observable.set<string>();
  private enabledUserTests = observable.set<string>();
  private _usersModals: UserModal[] = observable.array([], {deep: false});

  constructor() {
    makeAutoObservable(this);
  }

  setCurrentTime(currentTime: number) {
    this._currentTime = currentTime;
  }

  get currentTime() {
    return this._currentTime;
  }

  openProfileModal(modal: ProfileModal) {
    this.activeProfileModals.add(modal);
  }

  closeProfileModal(modal: ProfileModal) {
    this.activeProfileModals.delete(modal);
  }

  isProfileModalOpen(modal: ProfileModal) {
    return this.activeProfileModals.has(modal);
  }

  get hasActiveProfileModal() {
    return this.activeProfileModals.size > 0;
  }

  addSignUpAbTests(names: string[]) {
    for (const name of names) {
      this.enabledSignUpTests.add(name);
    }
  }

  addUserAbTests(names: string[]) {
    for (const name of names) {
      this.enabledUserTests.add(name);
    }
  }

  replaceUserAbTests(names: string[]) {
    if (isArraySimpleEqual(names, [...this.enabledUserTests])) {
      return;
    }

    this.enabledUserTests.replace(names);
  }

  activeAbTest(name: AVAILABLE_AB_TESTS): boolean {
    return this.enabledUserTests.has(name) || this.enabledSignUpTests.has(name);
  }

  clearAbTests() {
    this.enabledSignUpTests.clear();
    this.enabledUserTests.clear();
  }

  openFilterModal(name: FiltersModalView) {
    this.activeFilterModalView = name;
  }

  closeFilterModal() {
    this.activeFilterModalView = undefined;
  }

  get usersModals() {
    return this._usersModals;
  }

  addUserModal(modalUser: UserModal) {
    if (this._usersModals.some((user) => user.user.id === modalUser.user.id)) {
      return;
    }

    this._usersModals.push(modalUser);
  }

  removeUserModal(modalUser: UserModal) {
    this._usersModals = this._usersModals.filter(
      (user) => user.user.id !== modalUser.user.id
    );
  }
}

const store = new AppStore();

autoupdateCurrentTime(() => {
  store.setCurrentTime(Date.now());
}, 10 * ONE_SECOND_MS);

export default store;
